import React from "react";
import PropTypes from "prop-types";
import {
  Dispatcher,
  NavigatorStore,
  NavigatorAction,
  BookStore
} from "../../../flux";
import { Isvg } from "../../../components";

import Router from "../router";
import Header from "../header";
import BookReader from "../book-reader/book-reader";
import AssignmentNew from "./assignment-new";
import AssignmentSuccess from "./assignment-success";
import PageRange from "./readings/page-range";
import StartEnd from "./readings/start-end";
import ContentsSelection from "./readings/contents-selection";
import EasyToc from "./readings/easy-toc";

class AssignmentCreatorChrome extends React.Component {
  constructor(props) {
    super(props);
    const state = {
      status: "opened",
      firstDrop: true,
      book: {},
      assignment: {
        vbid: this.props.isbn
      },
      tocLoaded: false,
      pagesLoaded: false,
      readings: [],
      error: null,
      saving: false,
      feenyReady: false,
      toCancel: [],
      pinHeight: 20,
      startPin: null,
      endPin: null,
      showStartPin: false,
      showEndPin: false,
      scroll: {
        scrollTop: 0,
        scrollLeft: 0
      },
      pinPositionStyles: {},
      returnUrl: Fraction.Helpers.Locations.getParameterByName("returnUrl"),
      pageLoaded: false
    };
    this.state = Object.assign(state, this.props.savedState);
  }

  componentWillUnmount() {
    this.state.toCancel.forEach(cancel => cancel());
    Dispatcher.unbind(
      Dispatcher.BOOK_PAGE_SCROLL_FULLSPEED,
      "creator-scroll-fullspeed"
    );
    Dispatcher.unbind(Dispatcher.BOOK_PAGE_READY, "creator-book-page-ready");
    Dispatcher.unbind(
      Dispatcher.BOOK_PLACEHOLDER_RESIZE,
      "update-pin-location"
    );
    Dispatcher.unbind(Dispatcher.BOOK_MOUSE_WHEEL, "mouse-wheel-pin-location");
    Dispatcher.unbind(
      Dispatcher.BOOK_NAVIGATION_REQUESTED,
      "creator-book-navigation-requested"
    );
    Dispatcher.unbind(Dispatcher.BOOK_PAGE_LOAD, "creator-book-page-load");
    const viewer = this.state.book.viewer;
    viewer && viewer.Viewport.setAutofocus(false);
  }

  componentDidMount() {
    Dispatcher.bind(
      Dispatcher.BOOK_PLACEHOLDER_RESIZE,
      this._updatePinLocation.bind(this),
      "update-pin-location"
    );
    Dispatcher.bind(
      Dispatcher.BOOK_PAGE_SCROLL_FULLSPEED,
      (event, data, scroll) => {
        this.setState({ scroll });
        this._updatePinLocation();
      },
      "creator-scroll-fullspeed"
    );
    Dispatcher.bind(
      Dispatcher.BOOK_MOUSE_WHEEL,
      () => {
        this._updatePinLocation();
      },
      "mouse-wheel-pin-location"
    );

    Dispatcher.bind(
      Dispatcher.BOOK_PAGE_READY,
      this._showOrHidePins.bind(this),
      "creator-book-page-ready"
    );

    Dispatcher.bind(
      Dispatcher.BOOK_NAVIGATION_REQUESTED,
      () => {
        this.setState({ pageLoaded: false });
      },
      "creator-book-navigation-requested"
    );

    Dispatcher.bind(
      Dispatcher.BOOK_PAGE_LOAD,
      () => {
        this.setState({ pageLoaded: true });
      },
      "creator-book-page-load"
    );

    this.initFeeny();
    Dispatcher.bindOnce(Dispatcher.BOOK_CHANGE, () => {
      document.title += `: ${i18n.t("assignments.assignment-creator")}`;
    });
  }

  initFeeny() {
    const feenyInit = Feeny.init({ cancelable: true });
    const toCancel = this.state.toCancel;
    toCancel.push(feenyInit.cancel.bind(feenyInit));
    const book = BookStore.get(this.props.isbn);
    this.setState(
      {
        toCancel,
        status: "opened",
        book
      },
      this.saveState
    );
    book.viewer.Viewport.setAutofocus(false);
    feenyInit.promise
      .then(() => {
        this.setState({ feenyReady: true });
        this.loadAssignment();
      })
      .catch(() => this._close());
  }

  loadAssignment() {
    if (this.props.assignmentId) {
      Feeny.Assignments.one(this.props.assignmentId)
        .then(result =>
          this.setState({
            assignment: result.data,
            readings: result.data.readings
          })
        )
        .catch(error => {
          Fraction.Helpers.toast(i18n.t("assignments.no-edit-permission"), {
            type: "error"
          });
          Dispatcher.emit(Dispatcher.CHANGE_CHROME, "");
        });
    }
  }

  saveState(state) {
    if (typeof this.props.saveState === "function") {
      if (state) {
        this.props.saveState(Object.assign(this.state, state));
      } else {
        this.props.saveState(this.state);
      }
    }
  }

  clearState() {
    if (typeof this.props.saveState === "function") {
      this.props.saveState({});
    }
  }

  _minimize() {
    this.setState({ status: "minimized" }, () => {
      this.saveState();
      Dispatcher.emit(Dispatcher.SCRUBBER_OPEN);
      Dispatcher.emit(Dispatcher.CHANGE_CHROME, "");
    });
  }

  _close() {
    NavigatorAction.reset();
    this.clearState();
    Fraction.Helpers.clearRedirect();
    Fraction.Helpers.redirectMe();
  }

  _createOrUpdateAssignment() {
    this.setState({ saving: true });
    const assignment = this.state.assignment;
    assignment.title = assignment.title || i18n.t("assignments.untitled");
    if (this.state.feenyReady) {
      if (assignment.uuid) {
        return this._updateAssignment();
      }
      return Feeny.Assignments.create(assignment)
        .then(assignment => {
          this.setState({ assignment: assignment.data });
          return Feeny.UserAssignments.create(assignment.data.uuid);
        })
        .then(() => this.setState({ saving: false }))
        .catch(error => this.setState({ saving: false, error }));
    }
  }

  _updateAssignment() {
    this.setState({ saving: true });
    return Feeny.Assignments.update(
      this.state.assignment.uuid,
      this.state.assignment
    )
      .then(assignment => {
        this.setState({
          assignment: assignment.data,
          saving: false
        });
      })
      .catch(error => this.setState({ saving: false, error }));
  }

  _updateReading(uuid, reading) {
    if (this.state.assignment.uuid && this.state.feenyReady) {
      this.setState({ saving: true });
      return Feeny.Readings.update(this.state.assignment.uuid, uuid, reading)
        .then(reading => {
          const readings = this.state.readings.filter(
            reading => reading.uuid !== uuid
          );
          readings.push(reading.data);
          this.setState({
            readings,
            saving: false
          });
        })
        .catch(error => this.setState({ saving: false, error }));
    }
  }

  _createReading(reading, meta) {
    if (
      !this.state.saving &&
      this.state.assignment.uuid &&
      this.state.feenyReady
    ) {
      this.setState({ saving: true });
      reading.order = this.state.readings.length + 1;
      return Feeny.Readings.create(this.state.assignment.uuid, reading, meta)
        .then(reading => {
          return Feeny.UserReadings.create(
            this.state.assignment.uuid,
            reading.data.uuid
          ).then(response => {
            const readings = this.state.readings;
            readings.push(response.data);
            this.setState({ saving: false, readings });
          });
        })
        .catch(error => this.setState({ saving: false, error }));
    }
  }

  _removeReading(uuid, button) {
    Fraction.Helpers.showModal(
      {
        title: i18n.t("reader.notebook.delete-reading-header"),
        content: (
          <span>{i18n.t("reader.notebook.note-delete-confirmation")}</span>
        ),
        buttons: [
          {
            label: i18n.t("base.cancel"),
            click: Fraction.Helpers.removeModal,
            color: "gray"
          },
          {
            label: i18n.t("base.delete"),
            click: function() {
              Fraction.Helpers.removeModal();
              if (this.state.feenyReady && this.state.assignment.uuid) {
                this.setState({ saving: true });
                Feeny.Readings.delete(this.state.assignment.uuid, uuid).then(
                  () => {
                    const element =
                      this.state.readings.length > 1
                        ? document.getElementById("readings")
                        : document.getElementById("add-readings");
                    element && element.focus();
                    const readings = this.state.readings.filter(
                      data => data.uuid !== uuid
                    );
                    this.setState({ readings, saving: false });
                  }
                );
              }
            }.bind(this)
          }
        ]
      },
      button
    );
  }

  _handleInputChange(input) {
    this.setState({
      assignment: Object.assign({}, this.state.assignment, input)
    });
  }

  _showOrHidePins() {
    const { startPin, endPin } = this.state;
    const startCFI = startPin && startPin.cfi;
    const endCFI = endPin && endPin.cfi;
    const pageCFI = this.state.book.pageCFI;
    const startRelativeCFI = startCFI && startCFI.split("!").shift();
    const endRelativeCFI = endCFI && endCFI.split("!").shift();
    this.setState(
      {
        showStartPin:
          Jigsaw.CFIUtils.compareCFIs(pageCFI, startRelativeCFI) === 0,
        showEndPin: Jigsaw.CFIUtils.compareCFIs(pageCFI, endRelativeCFI) === 0
      },
      () => {
        this._updatePinLocation();
      }
    );
  }

  _dropPin(target, pos, cfi) {
    Fraction.Brand.Reader.highlighting = true;
    const { firstDrop, startPin, endPin, scroll } = this.state;
    let dropped = false;
    if (firstDrop) {
      Fraction.Helpers.toast(i18n.t("assignments.pins-drop-message"));
      this.setState({ firstDrop: false });
    }
    if (target === "start" && pos) {
      if (
        endPin &&
        endPin.cfi &&
        Jigsaw.CFIUtils.compareCFIs(endPin.cfi, cfi) === -1
      ) {
        Fraction.Helpers.toast(i18n.t("assignments.start-pin-drop-error"), {
          type: "error"
        });
        this.setState({
          startPin: null,
          showStartPin: false
        });
      } else {
        this.setState({
          startPin: {
            cfi,
            top: scroll.scrollTop + pos.top,
            left: scroll.scrollLeft + pos.left
          },
          showStartPin: true
        });
        dropped = true;
      }
    } else if (target === "end" && pos) {
      if (Jigsaw.CFIUtils.compareCFIs(cfi, startPin.cfi) === -1) {
        Fraction.Helpers.toast(i18n.t("assignments.end-pin-drop-error"), {
          type: "error"
        });
        this.setState({
          endPin: null,
          showEndPin: false
        });
      } else {
        this.setState({
          endPin: {
            cfi,
            top: scroll.scrollTop + pos.top + pos.height,
            left: scroll.scrollLeft + pos.left,
            right: pos.right
          },
          showEndPin: true
        });
        dropped = true;
      }
    }
    if (dropped) {
      Fraction.Viewer.Viewport.resetCursor();
    }
    return dropped;
  }

  _swapReadings(origin, destiny) {
    let readings = this.state.readings;
    let index1 = null;
    let index2 = null;
    for (let i = 0; i < readings.length; i++) {
      if (readings[i].uuid === origin) {
        index1 = i;
      }
      if (readings[i].uuid === destiny) {
        index2 = i;
      }
    }
    let reading1 = readings[index1];
    let reading2 = readings[index2];
    let order1 = reading1.order;
    let order2 = reading2.order;
    reading1.order = order2;
    reading2.order = order1;
    readings[index1] = reading2;
    readings[index2] = reading1;
    Feeny.Readings.update(this.state.assignment.uuid, reading1.uuid, reading1);
    Feeny.Readings.update(this.state.assignment.uuid, reading2.uuid, reading2);
    this.setState({ readings });
  }

  _clearStartPin() {
    Fraction.Brand.Reader.highlighting = false;
    this.setState({ startPin: null, showStartPin: false });
  }

  _clearEndPin() {
    Fraction.Brand.Reader.highlighting = false;
    this.setState({ endPin: null, showEndPin: false });
  }

  _clearPins() {
    this.setState({
      startPin: null,
      endPin: null,
      showEndPin: false,
      showStartPin: false
    });
  }

  _updatePinLocation() {
    const placeholder = $("#jigsaw-placeholder");
    const chromePlaceholder = $("#book-reader.chrome-side");
    let ariaHidden = false;
    if (placeholder.size() > 0) {
      const placeholderOffset = placeholder.offset();
      const chromeOffset = chromePlaceholder.offset();
      if (placeholderOffset) {
        this.setState({
          pinPositionStyles: {
            top: placeholderOffset.top - chromeOffset.top,
            left: placeholderOffset.left - chromeOffset.left,
            width: placeholder.width(),
            height: placeholder.height()
          }
        });
      }
    }
  }

  renderRoutes() {
    return (
      <Router>
        <AssignmentNew
          route="new-assignment"
          isbn={this.props.isbn}
          assignment={this.state.assignment}
          readings={this.state.readings}
          saving={this.state.saving}
          feenyReady={this.state.feenyReady}
          onTextChange={this._handleInputChange.bind(this)}
          createOrUpdateAssignment={this._createOrUpdateAssignment.bind(this)}
          removeReading={this._removeReading.bind(this)}
          saveState={this.saveState.bind(this)}
          savedState={this.state["new-assignment"]}
          swapReadings={this._swapReadings.bind(this)}
          tocLoaded={this.state.tocLoaded}
          pagesLoaded={this.state.pagesLoaded}
        />
        <PageRange
          route="page-range"
          isbn={this.props.isbn}
          book={this.state.book}
          assignment={this.state.assignment}
          saving={this.state.saving}
          feenyReady={this.state.feenyReady}
          totalReadings={this.state.readings.length}
          createReading={this._createReading.bind(this)}
          updateReading={this._updateReading.bind(this)}
          createOrUpdateAssignment={this._createOrUpdateAssignment.bind(this)}
          saveState={this.saveState.bind(this)}
          savedState={this.state["page-range"]}
        />
        <StartEnd
          route="start-end"
          isbn={this.props.isbn}
          book={this.state.book}
          assignment={this.state.assignment}
          saving={this.state.saving}
          feenyReady={this.state.feenyReady}
          totalReadings={this.state.readings.length}
          createReading={this._createReading.bind(this)}
          updateReading={this._updateReading.bind(this)}
          dropPin={this._dropPin.bind(this)}
          clearPins={this._clearPins.bind(this)}
          clearStartPin={this._clearStartPin.bind(this)}
          clearEndPin={this._clearEndPin.bind(this)}
          createOrUpdateAssignment={this._createOrUpdateAssignment.bind(this)}
          saveState={this.saveState.bind(this)}
          savedState={this.state["start-end"]}
        />
        <ContentsSelection
          route="contents-selection"
          isbn={this.props.isbn}
          book={this.state.book}
          assignment={this.state.assignment}
          saving={this.state.saving}
          feenyReady={this.state.feenyReady}
          totalReadings={this.state.readings.length}
          createReading={this._createReading.bind(this)}
          updateReading={this._updateReading.bind(this)}
          createOrUpdateAssignment={this._createOrUpdateAssignment.bind(this)}
          saveState={this.saveState.bind(this)}
          savedState={this.state["contents-selection"]}
        />
        <EasyToc
          route="easy-toc"
          book={this.state.book}
          createReading={this._createReading.bind(this)}
          createOrUpdateAssignment={this._createOrUpdateAssignment.bind(this)}
          saving={this.state.saving}
          hasAssignment={this.state.assignment && this.state.assignment.uuid}
        />
        <AssignmentSuccess
          route="success"
          isbn={this.props.isbn}
          assignment={this.state.assignment}
          saving={this.state.saving}
          feenyReady={this.state.feenyReady}
          onPreviewClick={() => alert("onPreviewClick")}
          saveState={this.saveState.bind(this)}
          savedState={this.state["success"]}
          returnUrl={this.state.returnUrl}
        />
      </Router>
    );
  }

  renderGoTo(target, position) {
    const { scroll, startPin, endPin } = this.state;
    if (target === "start" && this.state.endPin) {
      const pinSize = 40;
      const header = 50;
      const footer = 50;
      const windowHeight = $(window).height() - header - footer;
      if (
        windowHeight + scroll.scrollTop < endPin.top - pinSize ||
        !this.state.showEndPin
      ) {
        return (
          <button
            className="noButton goto-button goto-end"
            onClick={() =>
              this.state.book.viewer.Book.goToCFI(this.state.endPin.cfi)
            }
          >
            <Isvg src={`/img/down_arrow.svg`} />
          </button>
        );
      }
    } else if (target === "end") {
      if (startPin && scroll) {
        const startTopPosition = startPin.top - scroll.scrollTop;
        if (startTopPosition <= 0 || !this.state.showStartPin) {
          return (
            <button
              className="noButton goto-button goto-start"
              onClick={() =>
                this.state.book.viewer.Book.goToCFI(this.state.startPin.cfi)
              }
            >
              <Isvg src={`/img/up_arrow.svg`} />
            </button>
          );
        }
      }
    }
  }

  renderPin(pin, position) {
    const shouldRender = {
      start: this.state.showStartPin,
      end: this.state.showEndPin
    };
    if (shouldRender[pin]) {
      const style = {
        height: this.state.pinHeight
      };
      return (
        <div
          className={`markerPin ${pin}Pin`}
          style={Object.assign({}, style, position)}
        >
          <Isvg
            className="pin"
            src={`/img/assignments/assets/img/assignments_${pin}_pin.svg`}
          />
          {this.renderGoTo(pin, position)}
        </div>
      );
    }
  }

  render() {
    const startPinPosition = this.state.startPin && {
      top: this.state.startPin.top - this.state.scroll.scrollTop,
      left: this.state.startPin.left - this.state.scroll.scrollLeft
    };
    const endPinPosition = this.state.endPin && {
      top:
        this.state.endPin.top -
        this.state.scroll.scrollTop -
        this.state.pinHeight,
      left: this.state.endPin.right
    };
    const routestack = NavigatorStore.get();
    const lastRoute = routestack[routestack.length - 1];
    const isSuccess = lastRoute && lastRoute.route === "success";
    return (
      <div id="assignments">
        {!this.state.pageLoaded && (
          <div id="invisible-loader-for-selenium-tests" />
        )}
        <div id="creator-navigation">
          <div className="chrome-content">
            {!isSuccess && (
              <Header
                title={i18n.t("assignments.assignment-creator")}
                minimize={this._minimize.bind(this)}
                focus={false}
                close={this._close.bind(this)}
                hideBackButton={true}
                returnUrl={this.state.returnUrl}
              />
            )}
            <div
              className={`chrome-side chrome-right ${
                isSuccess ? "success" : ""
              }`}
            >
              {this.state.feenyReady && this.renderRoutes()}
            </div>

            <BookReader
              className="chrome-side"
              book={this.state.book}
              minimize={this._minimize.bind(this)}
            >
              <div
                className="pinContainer"
                style={this.state.pinPositionStyles}
              >
                {this.renderPin("start", startPinPosition)}
                {this.renderPin("end", endPinPosition)}
              </div>
              {this.props.children}
            </BookReader>
          </div>
        </div>
      </div>
    );
  }
}

AssignmentCreatorChrome.propTypes = {
  isbn: PropTypes.string.isRequired,
  assignmentId: PropTypes.string,
  readings: PropTypes.array,
  assignment: PropTypes.object
};

module.exports = {
  class: AssignmentCreatorChrome,
  metadata: () => {
    return {
      id: "assignment-creator-chrome",
      name: i18n.t("assignments.assignment-creator"),
      description: i18n.t("assignments.description"),
      type: "chrome"
    };
  }
};
